export function generateGameplan(responses) {
  return {
    fiveOnes: {
      targetMarket: responses.target_market || 'Not specified',
      offer: responses.offer || 'Not specified',
      conversionMechanism: responses.conversion_mechanism || 'Not specified',
      problemSolved: responses.problem_solved || 'Not specified',
      launchDate: responses.launch_date || 'Not specified',
      pricePoint: responses.price_point || 0
    },
    
    insights: {
      bestMonth: responses.best_month_revenue || 'Not specified',
      successFactors: responses.success_factors || 'Not specified',
      topContent: Array.isArray(responses.top_content_types) 
        ? responses.top_content_types.join(', ') 
        : 'Not specified',
      growthStrategies: responses.audience_growth || 'Not specified'
    },
    
    revenue: calculateRevenue(responses),
    
    content: {
      postingLevel: getPostingLevel(responses.posting_level),
      schedule: getPostingSchedule(responses.posting_level),
      topPerformingTypes: Array.isArray(responses.top_content_types) 
        ? responses.top_content_types 
        : []
    },
    
    actions: {
      daily: {
        priority: Array.isArray(responses.daily_priority_tasks) 
          ? responses.daily_priority_tasks.map(formatTaskLabel)
          : [],
        extra: Array.isArray(responses.daily_extra_tasks) 
          ? responses.daily_extra_tasks.map(formatTaskLabel)
          : []
      },
      weekly: [
        ...(Array.isArray(responses.weekly_priority_tasks) 
          ? responses.weekly_priority_tasks.map(formatTaskLabel)
          : []),
        ...(Array.isArray(responses.weekly_extra_tasks) 
          ? responses.weekly_extra_tasks.map(formatTaskLabel)
          : [])
      ],
      monthly: [
        ...(Array.isArray(responses.monthly_priority_tasks) 
          ? responses.monthly_priority_tasks.map(formatTaskLabel)
          : []),
        ...(Array.isArray(responses.monthly_extra_tasks) 
          ? responses.monthly_extra_tasks.map(formatTaskLabel)
          : [])
      ]
    },
    
    commitment: {
      word: responses.word_for_2026 || 'Not specified',
      showUp: responses.how_show_up || 'Not specified',
      nonNegotiable: responses.non_negotiable || 'Not specified',
      endOfYear: responses.end_of_year_vision || 'Not specified',
      why: responses.why_2026 || 'Not specified'
    }
  };
}

function calculateRevenue(responses) {
  const monthlyGoal = parseFloat(responses.monthly_income_goal) || 0;
  const pricePoint = parseFloat(responses.price_point) || 1;
  const conversionRate = parseFloat(responses.conversion_rate) || 2;
  
  const monthlySalesGoal = Math.ceil(monthlyGoal / pricePoint);
  const monthlyTrafficGoal = Math.ceil(monthlySalesGoal / (conversionRate / 100));
  const dailyTrafficGoal = Math.ceil(monthlyTrafficGoal / 30);
  
  return {
    monthlyGoal,
    averageOrderValue: pricePoint,
    conversionRate,
    monthlySalesGoal,
    monthlyTrafficGoal,
    dailyTrafficGoal
  };
}

function getPostingLevel(level) {
  switch (level) {
    case 'level_1':
      return 'Level 1: 1 post per day at 7 AM PST';
    case 'level_2':
      return 'Level 2: 2 posts per day (7 AM & 12 PM PST)';
    case 'level_3':
      return 'Level 3: 3 posts per day (7 AM, 12 PM & 4 PM PST)';
    default:
      return 'Level 1: 1 post per day at 7 AM PST';
  }
}

function getPostingSchedule(level) {
  switch (level) {
    case 'level_1':
      return ['1 piece of content published at 7 AM PST'];
    case 'level_2':
      return [
        '1 piece of content published at 7 AM PST',
        '1 piece of content published at 12 PM PST'
      ];
    case 'level_3':
      return [
        '1 piece of content published at 7 AM PST',
        '1 piece of content published at 12 PM PST',
        '1 piece of content published at 4 PM PST'
      ];
    default:
      return ['1 piece of content published at 7 AM PST'];
  }
}

function formatTaskLabel(taskValue) {
  const taskLabels = {
    // Daily tasks
    'post_content': 'Post scheduled content',
    'respond_comments': 'Respond to comments',
    'respond_dms': 'Respond to DMs',
    'engage_audience': 'Engage with audience',
    'research_content': 'Research new content ideas',
    'check_analytics': 'Check analytics',
    'follow_up_leads': 'Follow up with leads',
    'update_social_media': 'Update social media stories',
    'network_online': 'Network with other creators',
    'learn_new_skills': 'Learn new skills',
    'competitor_research': 'Research competitors',
    'brainstorm_ideas': 'Brainstorm new ideas',
    'organize_content': 'Organize content files',
    'plan_tomorrow': 'Plan tomorrow\'s tasks',
    
    // Weekly tasks
    'plan_content': 'Plan content for the week',
    'batch_create': 'Batch create content',
    'review_analytics': 'Review weekly analytics',
    'batch_film': 'Batch film videos/reels',
    'email_list': 'Send email to list',
    'goal_review': 'Review weekly goals',
    'shoot_broll': 'Shoot b-roll content',
    'organize_files': 'Organize files and assets',
    'research_trends': 'Research new trends',
    'collaborate': 'Reach out for collaborations',
    'update_website': 'Update website/landing pages',
    'celebrate_wins': 'Celebrate weekly wins',
    
    // Monthly tasks
    'review_best_content': 'Review last month\'s best content',
    'set_monthly_goals': 'Set monthly goals',
    'analyze_metrics': 'Analyze monthly metrics',
    'plan_content_calendar': 'Plan content calendar',
    'financial_review': 'Review financial performance',
    'strategy_adjustment': 'Adjust strategy',
    'celebrate_achievements': 'Celebrate achievements',
    'network_events': 'Attend networking events',
    'skill_development': 'Invest in skill development',
    'remove_unproductive': 'Remove unproductive tasks',
    'team_check_in': 'Team check-in',
    'competitor_analysis': 'Competitor analysis'
  };
  
  return taskLabels[taskValue] || taskValue;
}